import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {DateTime} from 'luxon';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {
  ScheduleNodeOutputData,
  ScheduleType,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {scheduleOutputSchema} from '../../../../schemas/scheduleCardSchema';
import _findLastIndex from 'lodash/findLastIndex';
import {ScheduleCardScratchpadState} from '../../scratchpad/scheduleCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import validateScheduleOutput from '../../utils/schedule/validateScheduleOutput';
import {UTCAbbreviation} from 'common/src/modules/timezones/timezones.consts';

const addScheduleOutputInputSchema = z.object({
  scheduleOutput: scheduleOutputSchema,
});

export default function addScheduleOutputTool() {
  async function execute(
    params: ToolExecuteParams<typeof addScheduleOutputInputSchema, ScheduleCardScratchpadState>,
  ) {
    const {scheduleOutput} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<ScheduleCardScratchpadState>(
      agentScratchpad,
      NodeType.SCHEDULE,
    );

    const timeZone = validatedScratchpad.card.timeZone?.trim() || UTCAbbreviation;
    const error = validateScheduleOutput(
      scheduleOutput,
      validatedScratchpad.card.scheduleType as ScheduleType,
      timeZone,
    );

    if (error) {
      return toolResult(JSON.stringify({status: 'error', message: error}));
    }

    const nextId =
      validatedScratchpad.card.outputs.length > 0
        ? Math.max(...validatedScratchpad.card.outputs.map((o) => o.id), 0) + 1
        : 1;

    const newOutput: ScheduleNodeOutputData = {
      id: nextId,
      nodeId: NO_NODE_ID,
      name: scheduleOutput.name ?? `Output ${nextId}`,
      type: OutputType.Regular,
      dateRange:
        scheduleOutput.dateRange && scheduleOutput.dateRange.start && scheduleOutput.dateRange.end
          ? {
              start: DateTime.fromISO(scheduleOutput.dateRange.start, {zone: timeZone}),
              end: DateTime.fromISO(scheduleOutput.dateRange.end, {zone: timeZone}),
            }
          : undefined,
      daysOfWeek:
        scheduleOutput.daysOfWeek && scheduleOutput.daysOfWeek.length > 0
          ? scheduleOutput.daysOfWeek.map((dow) => ({
              days: dow.days ?? [],
              start_minute: dow.start_minute ?? 0,
              end_minute: dow.end_minute ?? 0,
            }))
          : undefined,
    };

    const lastRegularIndex = _findLastIndex(
      validatedScratchpad.card.outputs,
      (output) => output.type === OutputType.Regular,
    );

    if (lastRegularIndex === -1) {
      validatedScratchpad.card.outputs.unshift(newOutput);
    } else {
      validatedScratchpad.card.outputs.splice(lastRegularIndex + 1, 0, newOutput);
    }

    return toolResult('Schedule output added');
  }

  return tool<typeof addScheduleOutputInputSchema, ScheduleCardScratchpadState>({
    name: V2CardAgentToolName.AddScheduleOutput,
    displayName: 'Adding schedule output',
    description: 'Add a schedule output (validated against schedule type).',
    inputSchema: addScheduleOutputInputSchema,
    execute,
  });
}
